import { useEffect, useRef, useState, useCallback } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { QuillBinding } from 'y-quill';
import { getSupabase, supabaseSchema } from '../../components/supabaseClient';
import { getCollabWSURL } from '../../components/env';
import activeDocumentTracker from '../../utils/activeDocumentTracker';

// Color palette for cursors / avatars. Hues are deliberately spread ~20-40°
// apart all the way around the wheel — NO two entries sit in the same hue
// family (the previous palette had three reds/pinks and three greens, which
// is why two users kept looking alike). All are Tailwind-600-weight so white
// avatar text and cursor carets stay legible on a light editor background.
// Ordered around the wheel: red → orange → gold → lime → green → teal → sky
// → blue → indigo → purple → fuchsia → pink.
//
// The actual per-user assignment (see pickDistinctColor below) is
// collision-aware: a joiner picks whichever entry maximizes minimum hue
// distance to colors already in use, and the awareness-change handler repairs
// near-collisions once peers materialize — so the first N joiners always land
// on clearly different shades.
//
// colorForUser is kept exported for back-compat with the unit tests and any
// deterministic-color callers; it is no longer the primary color source.
export const COLOR_PALETTE = [
  '#dc2626', // red
  '#ea580c', // orange
  '#ca8a04', // gold
  '#65a30d', // lime
  '#16a34a', // green
  '#0d9488', // teal
  '#0284c7', // sky
  '#2563eb', // blue
  '#4f46e5', // indigo
  '#9333ea', // purple
  '#c026d3', // fuchsia
  '#db2777', // pink
];

// WS handshake params. `schema` selects which Postgres schema the collab
// server seeds/persists the room's notes-c row from — for a cross-schema
// (linked-provider) patient this is the patient's home schema, not the
// viewer's active schema.
export function buildCollabParams({ token, schema, session, clientId }) {
  const params = { token: token || '', schema: schema || 'public', session };
  // The tab's own Yjs client id: the server's lineage check must not read
  // this tab's unsynced edits as a foreign history (see consumer.py).
  if (clientId != null) params.clientId = String(clientId);
  return params;
}

// The server closes the socket with one of these codes when the document
// this tab holds must NOT be merged into the room it would reconnect to:
//   4409  the tab's Yjs history is foreign to the room's (the collab server
//         came back hydrated from the other store, or from .json / the
//         seed) -- backend/api/collab/consumer.py LINEAGE_MISMATCH_CLOSE_CODE
//   4410  the document store changed under the room (RDS went down or came
//         back, RDS paused or enabled): the new store's copy is authoritative
//         -- backend/api/collab/rooms_registry.py STORE_CHANGED_CLOSE_CODE
// A normal reconnect would merge both histories and double the note, so the
// hook throws its Y.Doc away and rebuilds from the server's current copy.
export const LINEAGE_MISMATCH_CLOSE_CODE = 4409;
export const STORE_CHANGED_CLOSE_CODE = 4410;
const REBUILD_CLOSE_CODES = [LINEAGE_MISMATCH_CLOSE_CODE, STORE_CHANGED_CLOSE_CODE];

/** Pure: does this y-websocket 'connection-close' event demand a rebuild? */
export function shouldRebuildDocOnClose(event) {
  return REBUILD_CLOSE_CODES.includes(Number(event?.code));
}

export function colorForUser(userId) {
  if (!userId) return COLOR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) | 0;
  return COLOR_PALETTE[Math.abs(h) % COLOR_PALETTE.length];
}

function hexToHue(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 0;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return h;
}

function circularHueDistance(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

// Smallest hue gap between `hex` and any color in `takenHexes` (360 if the
// set is empty). Higher = more visually separated from everyone else.
function minHueDistance(hex, takenHexes) {
  if (!takenHexes || takenHexes.length === 0) return 360;
  const h = hexToHue(hex);
  return Math.min(...takenHexes.map((t) => circularHueDistance(h, hexToHue(t))));
}

// Returns the palette color that maximizes minimum hue distance to every
// color already in `taken`. Ties are broken deterministically by `seed`
// when provided (so the same user re-picking with the same peer set lands
// on the same color), randomly otherwise.
export function pickDistinctColor(taken, seed) {
  const takenHues = (Array.isArray(taken) ? taken : []).map(hexToHue);
  let bestScore = -1;
  let bestEntries = [];
  for (const hex of COLOR_PALETTE) {
    const hue = hexToHue(hex);
    const score = takenHues.length === 0
      ? 360
      : Math.min(...takenHues.map((t) => circularHueDistance(hue, t)));
    if (score > bestScore) {
      bestScore = score;
      bestEntries = [hex];
    } else if (score === bestScore) {
      bestEntries.push(hex);
    }
  }
  if (seed) {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
    return bestEntries[Math.abs(h) % bestEntries.length];
  }
  return bestEntries[Math.floor(Math.random() * bestEntries.length)];
}

// Multi-tab dedup: keep the entry with the freshest interaction so the
// avatar reflects the active tab's cursor. Exported for unit testing —
// the hook calls this on every awareness 'change' event.
export function dedupeAwarenessStates(states) {
  const iter = typeof states?.forEach === 'function'
    ? { forEach: (cb) => states.forEach(cb) }
    : { forEach: (cb) => (states || []).forEach(cb) };
  const byUser = new Map();
  iter.forEach((state) => {
    if (!state || !state.userId) return;
    const prev = byUser.get(state.userId);
    if (!prev || (state.lastInteractionAt || 0) > (prev.lastInteractionAt || 0)) {
      byUser.set(state.userId, state);
    }
  });
  return Array.from(byUser.values());
}

// ── Reconnection tuning ──────────────────────────────────────────────────
//
// How long the socket may be down before we lock the editor. Routine
// reconnects finish well inside a second; locking instantly would strobe
// the editor read-only and steal the caret on every blip. Yjs buffers
// local edits in the YDoc meanwhile, so nothing is at risk in this window
// — it's only a *prolonged* outage that can lose work.
export const TYPING_LOCK_GRACE_MS = 1200;

// If we're still down after this long, y-websocket's own retry loop is not
// going to get us back on its own, for either of two reasons:
//   - A handshake the server REJECTS (an expired JWT closes it 4003 before
//     accept) never sets `wsconnected`, so the provider just increments
//     `wsUnsuccessfulReconnects` and retries forever at `maxBackoffTime`
//     — 2.5s apart, with the same dead token, indefinitely.
//   - A socket wedged in CONNECTING never fires `onclose` at all, and
//     y-websocket's own watchdog only acts `if (this.wsconnected)`, so
//     nothing ever tears it down.
// Both are invisible to the provider and both are what "it never comes
// back until I refresh the page" actually looks like. Recovery below
// forces a fresh attempt with a freshly-minted token.
export const STUCK_RECOVERY_MS = 15000;

// Supabase access tokens are minted once per connection and go stale.
// getSession() is cheap (reads local storage) and renews when near expiry.
const TOKEN_REFRESH_MS = 4 * 60 * 1000;

// Cadence for the "how long have we been down" clock. Only runs while
// disconnected, so an ordinary session pays nothing for it.
const DOWNTIME_TICK_MS = 400;

// Minimum spacing between wake-triggered reconnect attempts.
const WAKE_NUDGE_THROTTLE_MS = 3000;

/**
 * The connection gates, as one pure function so the rules are testable
 * without standing up a socket.
 *
 * `enabled` is load-bearing in every branch: when the feature flag is off
 * the caller passes a null documentPath, no provider is ever built, and
 * `status` sits at its 'connecting' seed forever. Reading that as an
 * outage would lock the editor on a page that has no socket to wait for.
 *
 * @param {{enabled: boolean, status: string, downMs: number}} s
 */
export function deriveCollabGates({ enabled, status, downMs }) {
  const isConnected = !!enabled && status === 'connected';
  const down = !!enabled && !isConnected;
  return {
    isConnected,
    // Un-graced: the indicator should go red the moment the link drops.
    isReconnecting: down,
    // Graced: Yjs holds local edits in the YDoc while the socket is down,
    // so a sub-second reconnect risks nothing and must not be allowed to
    // yank the editor out from under the caret. Only a sustained outage
    // can actually lose work.
    editingLocked: down && downMs >= TYPING_LOCK_GRACE_MS,
    // Long enough that y-websocket's own retry loop is demonstrably not
    // getting us back — time to force a fresh socket with a fresh token.
    needsRecovery: down && downMs >= STUCK_RECOVERY_MS,
  };
}

// Stable per-tab token. The server's 15s grace window keys on
// (userId, sessionToken) so a reconnect from the same tab is treated as
// the same client, not a brand-new avatar — prevents flicker.
function getOrCreateSessionToken() {
  if (typeof window === 'undefined') return 'srv';
  try {
    let t = sessionStorage.getItem('medstudent-collab-session');
    if (!t) {
      t = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('medstudent-collab-session', t);
    }
    return t;
  } catch (_) {
    return `${Date.now()}`;
  }
}

// Current Supabase access token, or null. Supabase renews it internally
// when it's close to expiry, so calling this before a (re)connect is what
// keeps the handshake credential valid for the life of the page.
async function fetchAccessToken() {
  try {
    const sb = await getSupabase();
    const { data } = await sb.auth.getSession();
    return data?.session?.access_token || null;
  } catch (_) {
    return null;
  }
}

/**
 * Live-collab hook for the medstudent clinic page.
 *
 * Plumbing: local Yjs YDoc → y-websocket → server YDoc → other peers, with
 * Quill bound to `doc.getText('quill')` via y-quill. Awareness (cursors,
 * presence) flows over y-protocols/awareness on the same socket.
 *
 * Read-only callers still receive live updates and broadcast their own
 * cursor/presence — they just don't initiate document edits (the toolbar
 * disables editing controls upstream).
 *
 * `quill` should be the actual Quill instance (or null while DocumentBody
 * is still mounting). The caller is expected to track it in state so this
 * hook re-runs when Quill becomes available — using a ref hides the
 * transition and the selection-change listener never attaches.
 */
export function useDocumentCollab({ documentPath, userInfo, quill, readOnly, patient, schema }) {
  const [awarenessUsers, setAwarenessUsers] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [statusSince, setStatusSince] = useState(Date.now());
  const [provider, setProvider] = useState(null);
  // Whether this room has ever been connected. Purely so the UI can say
  // "Connecting" on a fresh open instead of "Reconnecting", which reads as
  // "something just broke" when in fact nothing has happened yet.
  const [hasConnected, setHasConnected] = useState(false);
  // Milliseconds the socket has been down, ticked by its own effect while
  // disconnected. Deriving this from Date.now() at render time (as the old
  // `isOffline` did) silently freezes: once the socket is stuck nothing
  // re-renders, so the elapsed value stops advancing and the UI never
  // escalates. It has to be state.
  const [downMs, setDownMs] = useState(0);
  // Bumped when the server refuses this tab's Yjs history (see
  // shouldRebuildDocOnClose): Effect A depends on it, so the bump tears the
  // provider + Y.Doc down and builds fresh ones that sync from the server.
  const [rebuildKey, setRebuildKey] = useState(0);
  // How many times that happened, for the page to tell the user.
  const [reloadCount, setReloadCount] = useState(0);

  const docRef = useRef(null);
  const providerRef = useRef(null);
  const statusRef = useRef('connecting');
  const lastRecoveryRef = useRef(0);
  const sessionTokenRef = useRef(getOrCreateSessionToken());
  // Mirrors `readOnly` for Effect A's connect callback without making
  // `readOnly` a dependency of Effect A — see the comment on that effect's
  // dependency array for why (toggling Edit must not tear down the socket).
  const readOnlyRef = useRef(readOnly);
  useEffect(() => { readOnlyRef.current = readOnly; }, [readOnly]);

  // ── Effect A: WebSocket provider lifecycle ───────────────────────────────
  useEffect(() => {
    if (!documentPath || !userInfo?.userId) return undefined;

    const doc = new Y.Doc();
    docRef.current = doc;

    const tokenPromise = fetchAccessToken();

    let cancelled = false;
    let localProvider = null;
    let unsubStatus = null;
    let unsubClose = null;
    let unsubAwareness = null;

    tokenPromise.then((accessToken) => {
      if (cancelled) return;

      const wsBase = getCollabWSURL();
      const url = `${wsBase}/ws/medstudent-doc`;
      const params = buildCollabParams({
        token: accessToken,
        schema: schema || supabaseSchema,
        session: sessionTokenRef.current,
        clientId: doc.clientID,
      });
      localProvider = new WebsocketProvider(url, documentPath, doc, { params, connect: true });
      providerRef.current = localProvider;

      const displayName = userInfo.name || userInfo.email || 'Medstudent';
      // Collision-aware pick: avoid colors that other peers are already
      // using. The first peer's awareness states typically aren't visible
      // yet at this point — the WS sync arrives after we set our state —
      // so the change handler below also runs the picker on every update
      // to repair collisions once peers materialize.
      const colorsFromPeers = (awareness) => {
        const out = [];
        awareness.getStates().forEach((state, cid) => {
          if (!state || cid === awareness.clientID) return;
          const c = state.user?.color || state.color;
          if (c) out.push(c);
        });
        return out;
      };
      const colorSeed = `${userInfo.userId}_${sessionTokenRef.current}`;
      const userColor = pickDistinctColor(
        colorsFromPeers(localProvider.awareness),
        colorSeed,
      );
      // `user.{name,color}` is the shape y-quill's QuillBinding hands to
      // the quill-cursors module — that's how peers get a colored caret +
      // hover-name flag. We keep the flat fields too so IconRail works.
      localProvider.awareness.setLocalState({
        userId: userInfo.userId,
        name: displayName,
        initials: (userInfo.initials || (userInfo.name || '?').slice(0, 2)).toUpperCase(),
        color: userColor,
        user: { name: displayName, color: userColor },
        cursorIndex: null,
        selectionRange: null,
        lastInteractionAt: null,
        readOnly: !!readOnlyRef.current,
        sessionToken: sessionTokenRef.current,
      });

      const onStatus = (e) => {
        const next = e.status === 'connected' ? 'connected'
          : e.status === 'connecting' ? 'connecting'
          : 'offline';
        if (statusRef.current !== next) {
          statusRef.current = next;
          setStatus(next);
          setStatusSince(Date.now());
        }
        if (next === 'connected') setHasConnected(true);
        // A drop is the moment the credential matters most: y-websocket
        // re-reads `provider.params` on every reconnect but we only ever
        // minted a token at mount, so race a fresh one in now. If it
        // doesn't land before the 100ms retry, that attempt uses the old
        // token and the next backoff step picks up the new value.
        if (next !== 'connected') {
          fetchAccessToken().then((token) => {
            if (cancelled || !token || providerRef.current !== localProvider) return;
            localProvider.params = { ...localProvider.params, token };
          });
        }
      };
      localProvider.on('status', onStatus);
      unsubStatus = () => localProvider.off('status', onStatus);

      const onClose = (event) => {
        if (cancelled || !shouldRebuildDocOnClose(event)) return;
        console.warn(`[collab] server closed ${event?.code}: the document store changed or the server `
          + 'holds a different history — rebuilding the editor from the server\'s current copy '
          + '(edits not yet on the server are parked in Version History)');
        setReloadCount((n) => n + 1);
        setRebuildKey((k) => k + 1);
      };
      localProvider.on('connection-close', onClose);
      unsubClose = () => localProvider.off('connection-close', onClose);

      const collectAwareness = () => {
        const states = localProvider.awareness.getStates();
        setAwarenessUsers(dedupeAwarenessStates(states));

        // Collision repair. We only consider peers with a LOWER clientID
        // (joined before us in the Yjs sense) so just the later joiner ever
        // moves — that strict ordering is what stops two clients re-picking
        // at once and oscillating. If a better-separated palette slot exists
        // versus those earlier peers, switch to it. "Better" is measured by
        // minimum hue distance, so this fixes near-collisions (e.g. red vs
        // rose), not only exact same-color clashes — and because we switch
        // ONLY when separation strictly improves, the value is monotonic and
        // converges (no flip-flopping).
        const myCid = localProvider.awareness.clientID;
        const myState = localProvider.awareness.getLocalState();
        const myColor = myState?.color;
        if (!myColor) return;
        const earlierColors = [];
        states.forEach((state, cid) => {
          if (!state || cid >= myCid) return;
          const peerColor = state.user?.color || state.color;
          if (peerColor) earlierColors.push(peerColor);
        });
        if (earlierColors.length === 0) return;
        const currentSep = minHueDistance(myColor, earlierColors);
        const best = pickDistinctColor(earlierColors, colorSeed);
        const bestSep = minHueDistance(best, earlierColors);
        // +1° epsilon guards against float noise / no-op churn.
        if (!best || best === myColor || bestSep <= currentSep + 1) return;
        localProvider.awareness.setLocalState({
          ...myState,
          color: best,
          user: { ...(myState.user || {}), color: best },
        });
      };
      localProvider.awareness.on('change', collectAwareness);
      unsubAwareness = () => localProvider.awareness.off('change', collectAwareness);
      collectAwareness();

      setProvider(localProvider);
    });

    if (patient?.id && userInfo?.userId) {
      activeDocumentTracker.initialize()
        .then(() => activeDocumentTracker.trackDocument(
          documentPath,
          patient.id,
          patient.patient_name || patient.name || 'Patient',
          userInfo.userId,
          userInfo.name || userInfo.email || 'Medstudent',
        ))
        .catch((e) => console.warn('[collab] active-doc track failed:', e?.message));
    }

    return () => {
      cancelled = true;
      try { unsubStatus && unsubStatus(); } catch (_) {}
      try { unsubClose && unsubClose(); } catch (_) {}
      try { unsubAwareness && unsubAwareness(); } catch (_) {}
      try { localProvider?.destroy(); } catch (_) {}
      try { docRef.current?.destroy(); } catch (_) {}
      providerRef.current = null;
      docRef.current = null;
      setProvider(null);
      // A torn-down provider is "connecting", not "still connected" — leave
      // the next room's status starting from a truthful baseline.
      statusRef.current = 'connecting';
      setStatus('connecting');
      setStatusSince(Date.now());
      setHasConnected(false);
      activeDocumentTracker.untrackDocument(documentPath).catch(() => {});
    };
    // `readOnly` is intentionally NOT a dependency here: this effect owns the
    // WebSocket connection + Yjs doc + QuillBinding lifecycle, and toggling
    // Edit/Done-editing must not tear that down and reconnect — that resyncs
    // the document from the server and resets Quill's scroll position (the
    // "clicking Edit jumps to top" bug). `readOnly` is only needed once, to
    // seed the initial awareness payload (via readOnlyRef above); ongoing
    // changes are pushed by the lightweight effect below instead.
    // `rebuildKey` IS a dependency on purpose: the lineage-mismatch close
    // (see shouldRebuildDocOnClose) must rebuild the Y.Doc, not just the
    // socket, which is exactly this effect's teardown + re-run.
  }, [
    documentPath, userInfo?.userId, userInfo?.name, userInfo?.email,
    userInfo?.initials, schema,
    patient?.id, patient?.patient_name,
    rebuildKey,
  ]);

  // ── Effect A.5: push readOnly into awareness without reconnecting ────────
  useEffect(() => {
    if (!provider) return undefined;
    const state = provider.awareness.getLocalState() || {};
    provider.awareness.setLocalState({ ...state, readOnly: !!readOnly });
  }, [provider, readOnly]);

  // Is collab actually wired up for this render? When the feature flag is
  // off (or we're in safe-share/patient mode) the caller passes a null
  // documentPath and no socket is ever opened — status stays at its
  // 'connecting' seed forever, which must NOT be reported as an outage or
  // the editor would lock itself with no socket to wait for.
  const enabled = !!(documentPath && userInfo?.userId);

  // Anchor the downtime clock to the moment collab actually goes live.
  // `statusSince` seeds at first render, but documentPath usually isn't
  // known until the patient row loads — without this reset the clock would
  // count that unrelated wait, so the editor would lock the instant the
  // socket started connecting and the badge would open on "(8s)".
  const wasEnabledRef = useRef(false);
  useEffect(() => {
    if (enabled && !wasEnabledRef.current) setStatusSince(Date.now());
    wasEnabledRef.current = enabled;
  }, [enabled]);

  const gates = deriveCollabGates({ enabled, status, downMs });

  // ── Effect A.6: keep the handshake token fresh ───────────────────────────
  // y-websocket rebuilds its URL from `provider.params` on every reconnect
  // (see its `get url()`), but nothing ever updated the token we baked in
  // at mount. Once the Supabase JWT expired, every reconnect was closed
  // 4003 by the consumer before accept — and a rejected handshake never
  // sets `wsconnected`, so the provider retried forever at its 2.5s
  // backoff cap and never came back. Only a page reload (fresh token)
  // recovered. Refreshing here removes that failure mode entirely.
  useEffect(() => {
    if (!provider) return undefined;
    let cancelled = false;
    const refresh = async () => {
      const token = await fetchAccessToken();
      if (cancelled || !token) return;
      provider.params = { ...provider.params, token };
    };
    refresh();
    const id = setInterval(refresh, TOKEN_REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [provider]);

  // ── Effect A.7: downtime clock ───────────────────────────────────────────
  // Runs only while disconnected. Drives both the typing lock and the
  // stuck-socket recovery below, and gives the UI something that keeps
  // updating on its own during an outage.
  useEffect(() => {
    if (!enabled || status === 'connected') {
      setDownMs(0);
      return undefined;
    }
    setDownMs(Math.max(0, Date.now() - statusSince));
    const id = setInterval(
      () => setDownMs(Math.max(0, Date.now() - statusSince)),
      DOWNTIME_TICK_MS,
    );
    return () => clearInterval(id);
  }, [enabled, status, statusSince]);

  // ── Effect A.8: stuck-socket recovery ────────────────────────────────────
  // The escape hatch for the two states y-websocket cannot get itself out
  // of (see STUCK_RECOVERY_MS). `disconnect()` force-closes whatever
  // socket is there — including one wedged in CONNECTING, which is the
  // case its own watchdog can't touch — and `connect()` immediately opens
  // a new one using the token we just refreshed.
  //
  // Crucially this rebuilds only the *socket*: the Y.Doc, the QuillBinding
  // and every local edit made while offline survive untouched and sync up
  // once the connection lands.
  useEffect(() => {
    if (!gates.needsRecovery) return undefined;
    // Throttle rather than gate on the boolean alone. `needsRecovery`
    // latches true and stays true, so keying the effect only on it would
    // buy exactly ONE attempt per transition — and if `providerRef` is
    // still empty at that moment (a token fetch that hasn't resolved), we
    // would bail out and never look again, leaving the editor locked with
    // nothing retrying. Re-running on every downMs tick and spacing the
    // attempts here is what makes it keep trying.
    if (Date.now() - lastRecoveryRef.current < STUCK_RECOVERY_MS - DOWNTIME_TICK_MS) {
      return undefined;
    }
    const p = providerRef.current;
    if (!p) return undefined;
    lastRecoveryRef.current = Date.now();

    let cancelled = false;
    (async () => {
      const token = await fetchAccessToken();
      if (cancelled || providerRef.current !== p) return;
      if (token) p.params = { ...p.params, token };
      try { p.disconnect(); } catch (_) {}
      try { p.connect(); } catch (_) {}
      // Restart the clock explicitly. connect() re-emits 'connecting',
      // but if we were ALREADY 'connecting' that's not a transition, so
      // onStatus wouldn't touch statusSince and downMs would stay pinned
      // above the threshold — re-firing this effect on every tick.
      setStatusSince(Date.now());
    })();
    return () => { cancelled = true; };
  }, [gates.needsRecovery, downMs]);

  // ── Effect A.9: reconnect on wake ────────────────────────────────────────
  // The reported scenario — come back to a tab left sitting for a while.
  // A hidden tab has its timers throttled to roughly one tick a minute, so
  // y-websocket's backoff retries crawl; and if the machine slept, the
  // socket is dead without either side having noticed. Both resolve the
  // instant the user looks at the tab again, so retry immediately rather
  // than making them wait out a backoff step.
  useEffect(() => {
    if (!provider || typeof window === 'undefined') return undefined;
    let cancelled = false;
    let lastNudge = 0;
    const nudge = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (provider.wsconnected) return;
      // `focus` fires on every click back into the window. While we're
      // down that would restart the socket on each one, so space them out
      // — short enough to still feel instant when you return to the tab.
      if (Date.now() - lastNudge < WAKE_NUDGE_THROTTLE_MS) return;
      lastNudge = Date.now();
      const token = await fetchAccessToken();
      if (cancelled || providerRef.current !== provider) return;
      if (token) provider.params = { ...provider.params, token };
      if (provider.wsconnected) return;
      try { provider.disconnect(); } catch (_) {}
      try { provider.connect(); } catch (_) {}
      setStatusSince(Date.now());
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', nudge);
    }
    window.addEventListener('online', nudge);
    window.addEventListener('focus', nudge);
    return () => {
      cancelled = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', nudge);
      }
      window.removeEventListener('online', nudge);
      window.removeEventListener('focus', nudge);
    };
  }, [provider]);

  // ── Effect B: binding + selection tracking + cursor color sync ───────────
  // Runs whenever a Quill instance becomes available alongside a connected
  // provider. The selection-change listener writes `cursorIndex` into our
  // awareness state — that's what the IconRail click-to-scroll reads.
  useEffect(() => {
    if (!provider || !quill) return undefined;

    const ytext = docRef.current?.getText('quill');
    if (!ytext) return undefined;
    const binding = new QuillBinding(ytext, quill, provider.awareness);

    const onSel = (range) => {
      const state = provider.awareness.getLocalState() || {};
      provider.awareness.setLocalState({
        ...state,
        cursorIndex: range ? range.index : state.cursorIndex,
        selectionRange: range && range.length > 0
          ? { index: range.index, length: range.length }
          : null,
        lastInteractionAt: Date.now(),
      });
    };
    quill.on('selection-change', onSel);

    // quill-cursors v4 has two bugs that break per-user colors:
    //   1. `createCursor(id, name, color)` is a no-op when the cursor exists,
    //      so the FIRST awareness frame from a peer (which often lacks
    //      `user.color`) locks the cursor at y-quill's default `#ffa500`.
    //   2. The bundled CSS uses `attr(data-color)` for the caret bg, which
    //      only works in Chrome 133+; everywhere else the caret falls back
    //      to a default color (which we observed as blue).
    // We sidestep both by walking the cursors module's internal map after
    // each awareness change and force-writing inline styles based on the
    // peer's current `user.color` from awareness.
    const cursorsModule = quill.getModule?.('cursors');
    const syncCursorColors = () => {
      if (!cursorsModule) return;
      const states = provider.awareness.getStates();
      const internal = cursorsModule._cursors || {};
      states.forEach((state, clientId) => {
        if (!state) return;
        const id = String(clientId);
        const cur = internal[id];
        if (!cur) return;
        const desiredColor = state.user?.color || state.color || null;
        const desiredName = state.user?.name || state.name || null;
        if (!desiredColor && !desiredName) return;
        try {
          if (desiredColor) cur.color = desiredColor;
          if (desiredName) cur.name = desiredName;
          const root = cur._el || cur.el || null;
          if (!root) return;
          if (desiredColor) {
            // Use setProperty(..., 'important') so we beat both the bundled
            // `attr(data-color)` rule (Chrome 133+ only — falls back to a
            // browser default everywhere else) and any future CSS we
            // haven't accounted for. We ALSO override border-left-color
            // because documentComponents/document.css renders the caret as
            // a 4px left-border (not background) and hardcodes #007bff as
            // its default — the legacy doctor collab system overrides that
            // per-user, and the medstudent path needs to as well.
            const caret = root.querySelector('.ql-cursor-caret');
            const flag = root.querySelector('.ql-cursor-flag');
            if (caret) {
              caret.style.setProperty('background-color', desiredColor, 'important');
              caret.style.setProperty('border-left-color', desiredColor, 'important');
            }
            if (flag) flag.style.setProperty('background-color', desiredColor, 'important');
          }
          if (desiredName) {
            const nameEl = root.querySelector('.ql-cursor-name');
            if (nameEl && nameEl.textContent !== desiredName) nameEl.textContent = desiredName;
          }
        } catch (_) {
          /* keep going for other peers */
        }
      });
    };
    provider.awareness.on('change', syncCursorColors);
    // First pass — covers cursors that already exist when this effect runs.
    syncCursorColors();

    return () => {
      try { quill.off('selection-change', onSel); } catch (_) {}
      try { provider.awareness.off('change', syncCursorColors); } catch (_) {}
      try { binding.destroy(); } catch (_) {}
    };
  }, [provider, quill]);

  // When the user clicks an avatar we both scroll to their cursor AND
  // flash their name flag so the visual association between avatar and
  // caret is obvious — quill-cursors normally only shows the flag for
  // hideDelayMs after movement, which is gone by the time you click.
  const flashFlagForClientId = useCallback((clientId) => {
    const cursorsModule = quill?.getModule?.('cursors');
    const cur = cursorsModule?._cursors?.[String(clientId)];
    const root = cur?._el || cur?.el;
    if (!root) return;
    const container = root.querySelector('.ql-cursor-caret-container');
    if (!container) return;
    container.classList.add('hover');
    // Auto-hide after 4s. We use the same CSS class quill-cursors toggles
    // on real hover, so the visibility transition matches.
    setTimeout(() => container.classList.remove('hover'), 4000);
  }, [quill]);

  const scrollToUser = useCallback((userId) => {
    if (!provider || !quill) return { ok: false, reason: 'not-found' };
    // Resolve the peer's clientId from awareness — needed to find the
    // matching cursor element in the cursors module.
    let clientId = null;
    let user = null;
    provider.awareness.getStates().forEach((state, cid) => {
      if (state && state.userId === userId && cid !== provider.awareness.clientID) {
        clientId = cid;
        user = state;
      }
    });
    if (!user) return { ok: false, reason: 'not-found' };
    if (user.cursorIndex == null) return { ok: false, reason: 'no-cursor' };
    try {
      const bounds = quill.getBounds(user.cursorIndex);
      if (!bounds) return { ok: false, reason: 'no-bounds' };
      const editor = quill.root;
      // scrollIntoView on the editor root just centers the editor itself —
      // not what we want. Scroll the parent scroller by the cursor's
      // pixel offset within the editor. Fallback to the editor root if the
      // scroller can't be found.
      const scroller = editor.closest('[class*="overflow-y-auto"]') || editor.parentElement || editor;
      const editorTop = editor.getBoundingClientRect().top;
      const scrollerTop = scroller.getBoundingClientRect().top;
      const targetY = bounds.top + (editorTop - scrollerTop) - (scroller.clientHeight / 2);
      scroller.scrollTo({ top: scroller.scrollTop + targetY, behavior: 'smooth' });
      // Flash the name flag so the click visually lands on their cursor.
      if (clientId != null) flashFlagForClientId(clientId);
      return { ok: true };
    } catch (_) {
      return { ok: false, reason: 'error' };
    }
  }, [provider, quill, flashFlagForClientId]);

  return {
    awarenessUsers,
    scrollToUser,
    status,
    // Whether a socket is supposed to exist at all. Everything below is
    // gated on it so a collab-disabled page never reports an "outage".
    // Whether a socket is supposed to exist at all. Everything below is
    // gated on it so a collab-disabled page never reports an "outage".
    enabled,
    hasConnected,
    // isConnected / isReconnecting / editingLocked — see deriveCollabGates.
    ...gates,
    downMs,
    isOffline: gates.isReconnecting && downMs > 3000,
    statusSince,
    // Expose the live YDoc + the "quill" YText so callers that need to
    // atomically replace document contents (Copy-from-older modal in
    // MedStudentClinicPage) can transact through the CRDT instead of
    // calling Quill.setContents — which would echo the existing state
    // back through QuillBinding and double the content.
    getYDoc: () => docRef.current || null,
    getYText: () => docRef.current?.getText?.('quill') || null,
    // Times the server rejected this tab's document history and the editor
    // was rebuilt from the server's copy (shouldRebuildDocOnClose).
    reloadCount,
  };
}

export default useDocumentCollab;
